/**
 * Host Global - Housekeeping Dashboard
 * Displays reservations that are checked out and ready for housekeeping
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import apiRequest from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

function HousekeepingDashboard() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Housekeeping action dialogs
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [beforePhotoPreviews, setBeforePhotoPreviews] = useState([]);
  const [afterPhotoPreviews, setAfterPhotoPreviews] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch housekeeping tasks
  useEffect(() => {
    fetchTasks();
    // Refresh every 5 minutes
    const interval = setInterval(fetchTasks, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      const response = await apiRequest("/reservations/housekeeping/tasks");
      setTasks(response.tasks || []);
    } catch (error) {
      console.error("Error fetching housekeeping tasks:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load housekeeping tasks. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get multilingual text
  const getMultilingualText = (obj, fallback = "N/A") => {
    if (!obj) return fallback;
    if (typeof obj === "string") return obj;
    if (obj.en) return obj.en;
    if (obj.ar) return obj.ar;
    return fallback;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Full date + time (actual checkout or scheduled)
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  // Scheduled checkout time for pending (before guest has checked out)
  const getScheduledCheckOutTime = (task) => {
    if (task?.requestedCheckOutTime) {
      return formatTime(task.requestedCheckOutTime);
    }
    if (task?.earlyCheckout?.type === "sameDay" && task?.earlyCheckout?.requestedCheckOutTime) {
      return formatTime(task.earlyCheckout.requestedCheckOutTime);
    }
    return "12:00 PM";
  };

  // Get housekeeping status color
  const getHousekeepingStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "InProgress":
        return "info";
      case "Completed":
        return "success";
      default:
        return "default";
    }
  };

  // Display state for housekeeping: 🟡 Pending Checkout | 🟢 Ready for Cleaning | 🔵 In Progress | ✅ Completed
  const getTaskDisplayState = (task) => {
    if (task.status === "CheckedIn") {
      return {
        key: "pendingCheckout",
        label: "🟡 Pending Checkout",
        color: "warning",
        canStart: false,
      };
    }
    if (task.status !== "CheckedOut") {
      return { key: "unknown", label: task.status || "—", color: "default", canStart: false };
    }
    const hk = task.housekeeping?.status;
    if (hk === "Completed") {
      return {
        key: "completed",
        label: "✅ Cleaning Completed",
        color: "success",
        canStart: false,
      };
    }
    if (hk === "InProgress") {
      return {
        key: "inProgress",
        label: "🔵 Cleaning In Progress",
        color: "info",
        canStart: false,
      };
    }
    return {
      key: "readyForCleaning",
      label: "🟢 Ready for Cleaning",
      color: "success",
      canStart: true,
    };
  };

  // Counts for summary
  const pendingCheckoutCount = tasks.filter((t) => t.status === "CheckedIn").length;
  const readyCount = tasks.filter(
    (t) => t.status === "CheckedOut" && (!t.housekeeping || t.housekeeping.status === "Pending")
  ).length;
  const inProgressCount = tasks.filter((t) => t.housekeeping?.status === "InProgress").length;
  const completedCount = tasks.filter((t) => t.housekeeping?.status === "Completed").length;

  // Handle start housekeeping
  const handleStartHousekeeping = (task) => {
    setSelectedTask(task);
    setBeforePhotos([]);
    setBeforePhotoPreviews([]);
    setNotes("");
    setStartDialogOpen(true);
  };

  // Handle before photo selection
  const handleBeforePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + beforePhotos.length > 20) {
      setMessage({ type: "error", text: "Maximum 20 photos allowed" });
      return;
    }
    setBeforePhotos([...beforePhotos, ...files]);

    // Create previews
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setBeforePhotoPreviews([...beforePhotoPreviews, ...newPreviews]);
  };

  // Remove before photo
  const removeBeforePhoto = (index) => {
    const newPhotos = beforePhotos.filter((_, i) => i !== index);
    const newPreviews = beforePhotoPreviews.filter((_, i) => i !== index);
    setBeforePhotos(newPhotos);
    setBeforePhotoPreviews(newPreviews);
    // Revoke object URL to prevent memory leak
    URL.revokeObjectURL(beforePhotoPreviews[index]);
  };

  // Handle complete housekeeping
  const handleCompleteHousekeeping = (task) => {
    setSelectedTask(task);
    setAfterPhotos([]);
    setAfterPhotoPreviews([]);
    setNotes(task.housekeeping?.notes || "");
    setCompleteDialogOpen(true);
  };

  // Handle after photo selection
  const handleAfterPhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + afterPhotos.length > 20) {
      setMessage({ type: "error", text: "Maximum 20 photos allowed" });
      return;
    }
    setAfterPhotos([...afterPhotos, ...files]);

    // Create previews
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setAfterPhotoPreviews([...afterPhotoPreviews, ...newPreviews]);
  };

  // Remove after photo
  const removeAfterPhoto = (index) => {
    const newPhotos = afterPhotos.filter((_, i) => i !== index);
    const newPreviews = afterPhotoPreviews.filter((_, i) => i !== index);
    setAfterPhotos(newPhotos);
    setAfterPhotoPreviews(newPreviews);
    // Revoke object URL to prevent memory leak
    URL.revokeObjectURL(afterPhotoPreviews[index]);
  };

  // Submit start housekeeping
  const handleSubmitStart = async () => {
    if (beforePhotos.length === 0) {
      setMessage({ type: "error", text: "Please upload at least one before photo" });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const formData = new FormData();
      beforePhotos.forEach((photo, index) => {
        formData.append("beforePhotos", photo);
      });
      if (notes) {
        formData.append("notes", notes);
      }

      const response = await apiRequest(`/reservations/${selectedTask._id}/housekeeping/start`, {
        method: "PATCH",
        body: formData,
      });

      setMessage({ type: "success", text: "Housekeeping started successfully" });
      setStartDialogOpen(false);
      setSelectedTask(null);
      setBeforePhotos([]);
      setBeforePhotoPreviews([]);
      setNotes("");

      // Refresh tasks
      await fetchTasks();

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error starting housekeeping:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to start housekeeping. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Submit complete housekeeping
  const handleSubmitComplete = async () => {
    if (afterPhotos.length === 0) {
      setMessage({ type: "error", text: "Please upload at least one after photo" });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const formData = new FormData();
      afterPhotos.forEach((photo, index) => {
        formData.append("afterPhotos", photo);
      });
      if (notes) {
        formData.append("notes", notes);
      }

      const response = await apiRequest(`/reservations/${selectedTask._id}/housekeeping/complete`, {
        method: "PATCH",
        body: formData,
      });

      setMessage({ type: "success", text: "Housekeeping completed successfully" });
      setCompleteDialogOpen(false);
      setSelectedTask(null);
      setAfterPhotos([]);
      setAfterPhotoPreviews([]);
      setNotes("");

      // Refresh tasks
      await fetchTasks();

      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error completing housekeeping:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to complete housekeeping. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header – same style as Check-In/Check-Out dashboard */}
        <MDBox mb={4}>
          <MDTypography variant="h4" fontWeight="bold" mb={1}>
            Housekeeping Dashboard
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            Today&apos;s checkouts only. Pending Checkout = preparation only (no cleaning). Ready
            for Cleaning = upload before/after photos and complete cleaning.
          </MDTypography>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        {/* Summary – one Card with 4 stats, consistent with system */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <MDBox p={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "warning.lighter",
                    border: "1px solid",
                    borderColor: "warning.light",
                  }}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <MDBox>
                    <MDTypography
                      variant="body2"
                      color="text.secondary"
                      fontWeight="medium"
                      mb={0.5}
                    >
                      Pending Checkout
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="warning.dark">
                      {pendingCheckoutCount}
                    </MDTypography>
                  </MDBox>
                  <Icon sx={{ fontSize: "2.5rem", color: "warning.main", opacity: 0.6 }}>
                    schedule
                  </Icon>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "success.lighter",
                    border: "1px solid",
                    borderColor: "success.light",
                  }}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <MDBox>
                    <MDTypography
                      variant="body2"
                      color="text.secondary"
                      fontWeight="medium"
                      mb={0.5}
                    >
                      Ready for Cleaning
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="success.dark">
                      {readyCount}
                    </MDTypography>
                  </MDBox>
                  <Icon sx={{ fontSize: "2.5rem", color: "success.main", opacity: 0.6 }}>
                    check_circle
                  </Icon>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "info.lighter",
                    border: "1px solid",
                    borderColor: "info.light",
                  }}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <MDBox>
                    <MDTypography
                      variant="body2"
                      color="text.secondary"
                      fontWeight="medium"
                      mb={0.5}
                    >
                      Cleaning In Progress
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="info.dark">
                      {inProgressCount}
                    </MDTypography>
                  </MDBox>
                  <Icon sx={{ fontSize: "2.5rem", color: "info.main", opacity: 0.6 }}>
                    cleaning_services
                  </Icon>
                </MDBox>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MDBox
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "success.lighter",
                    border: "1px solid",
                    borderColor: "success.main",
                  }}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <MDBox>
                    <MDTypography
                      variant="body2"
                      color="text.secondary"
                      fontWeight="medium"
                      mb={0.5}
                    >
                      Cleaning Completed
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold" color="success.dark">
                      {completedCount}
                    </MDTypography>
                  </MDBox>
                  <Icon sx={{ fontSize: "2.5rem", color: "success.main", opacity: 0.6 }}>
                    done_all
                  </Icon>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </Card>

        {/* Tasks Table – same Card/Table style as Check-In/Check-Out */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <MDBox p={3}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
              flexWrap="wrap"
              gap={2}
            >
              <MDBox>
                <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
                  Today&apos;s Housekeeping Tasks
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  Showing {tasks.length} {tasks.length === 1 ? "task" : "tasks"} (checkout date
                  today)
                </MDTypography>
              </MDBox>
              <MDButton
                variant="outlined"
                color="info"
                startIcon={<Icon>refresh</Icon>}
                onClick={fetchTasks}
                disabled={loading}
                sx={{ minHeight: "44px" }}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </MDButton>
            </MDBox>

            {loading ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="320px">
                <CircularProgress />
              </MDBox>
            ) : tasks.length === 0 ? (
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={8}
              >
                <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>
                  cleaning_services
                </Icon>
                <MDTypography variant="h6" color="text.secondary" mb={1}>
                  No Tasks Today
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" textAlign="center">
                  No reservations with checkout date today. Tasks appear when checkout date is today
                  (Pending until guest checks out, then Ready for Cleaning).
                </MDTypography>
              </MDBox>
            ) : (
              <TableContainer
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "auto",
                  direction: "ltr",
                }}
              >
                <Table
                  sx={{
                    width: "100%",
                    tableLayout: "fixed",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    direction: "ltr",
                  }}
                  aria-label="Housekeeping tasks"
                >
                  <colgroup>
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "22%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: "grey.100",
                        "& .MuiTableCell-head": {
                          borderBottom: "2px solid",
                          borderColor: "divider",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                          py: 2,
                          px: 3,
                        },
                      }}
                    >
                      <TableCell sx={{ width: "16%", minWidth: "120px" }} align="left">
                        Reservation
                      </TableCell>
                      <TableCell sx={{ width: "24%", minWidth: "160px" }} align="left">
                        Unit / room
                      </TableCell>
                      <TableCell sx={{ width: "18%", minWidth: "140px" }} align="left">
                        Checkout time
                      </TableCell>
                      <TableCell sx={{ width: "22%", minWidth: "160px" }} align="left">
                        Status
                      </TableCell>
                      <TableCell sx={{ width: "20%", minWidth: "140px" }} align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasks.map((task) => {
                      const property = task.property || {};
                      const propertyTitle = getMultilingualText(property.title, "Unknown Property");
                      const displayState = getTaskDisplayState(task);
                      const isPendingCheckout = displayState.key === "pendingCheckout";
                      const reservationLabel = task.reservationNumber || task._id?.slice(-6) || "—";
                      const checkoutTimeDisplay =
                        task.status === "CheckedOut" && task.checkOut?.checkedOutAt
                          ? formatDateTime(task.checkOut.checkedOutAt)
                          : `${formatDate(task.checkOutDate)}, ${getScheduledCheckOutTime(task)}`;
                      return (
                        <TableRow
                          key={task._id}
                          sx={{
                            opacity: isPendingCheckout ? 0.9 : 1,
                            "& .MuiTableCell-root": {
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              py: 2,
                              px: 3,
                              verticalAlign: "middle",
                            },
                            "&:last-child .MuiTableCell-root": { borderBottom: "none" },
                          }}
                        >
                          <TableCell
                            align="left"
                            sx={{
                              width: "16%",
                              minWidth: "120px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 0,
                            }}
                          >
                            <MDTypography
                              variant="body2"
                              fontWeight="medium"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={reservationLabel}
                            >
                              {reservationLabel}
                            </MDTypography>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              width: "24%",
                              minWidth: "160px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 0,
                            }}
                          >
                            <MDTypography
                              variant="body2"
                              fontWeight="medium"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={propertyTitle}
                            >
                              {propertyTitle}
                            </MDTypography>
                            {property?.googleMapLink && (
                              <MDButton
                                variant="text"
                                color="info"
                                size="small"
                                startIcon={<Icon sx={{ fontSize: "1rem" }}>map</Icon>}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(property.googleMapLink, "_blank");
                                }}
                                sx={{ minHeight: "28px", p: 0, mt: 0.5 }}
                              >
                                Map
                              </MDButton>
                            )}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              width: "18%",
                              minWidth: "140px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 0,
                            }}
                          >
                            <MDTypography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {checkoutTimeDisplay}
                            </MDTypography>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              width: "22%",
                              minWidth: "160px",
                              overflow: "hidden",
                              maxWidth: 0,
                            }}
                          >
                            <Chip
                              label={displayState.label}
                              color={displayState.color}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                            {task.housekeeping?.startedAt && (
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                mt={0.5}
                              >
                                Started {formatTime(task.housekeeping.startedAt)}
                              </MDTypography>
                            )}
                            {task.housekeeping?.completedAt &&
                              task.housekeeping?.cleaningDurationMinutes != null && (
                                <MDTypography
                                  variant="caption"
                                  color="success.main"
                                  fontWeight="600"
                                  display="block"
                                  mt={0.5}
                                >
                                  {task.housekeeping.cleaningDurationMinutes < 60
                                    ? `${task.housekeeping.cleaningDurationMinutes} min`
                                    : `${Math.floor(
                                        task.housekeeping.cleaningDurationMinutes / 60
                                      )}h ${task.housekeeping.cleaningDurationMinutes % 60} min`}
                                </MDTypography>
                              )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ width: "20%", minWidth: "140px" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isPendingCheckout ? (
                              <MDTypography variant="caption" color="text.secondary">
                                No actions until guest checks out
                              </MDTypography>
                            ) : (
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-end"
                                gap={1}
                              >
                                {displayState.canStart && (
                                  <MDButton
                                    variant="gradient"
                                    color="warning"
                                    size="small"
                                    startIcon={<Icon>play_arrow</Icon>}
                                    onClick={() => handleStartHousekeeping(task)}
                                    sx={{ minHeight: "36px" }}
                                  >
                                    Start Cleaning
                                  </MDButton>
                                )}
                                {task.housekeeping?.status === "InProgress" && (
                                  <MDButton
                                    variant="gradient"
                                    color="success"
                                    size="small"
                                    startIcon={<Icon>check_circle</Icon>}
                                    onClick={() => handleCompleteHousekeeping(task)}
                                    sx={{ minHeight: "36px" }}
                                  >
                                    Complete Cleaning
                                  </MDButton>
                                )}
                              </MDBox>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </MDBox>
        </Card>
      </MDBox>

      {/* Start Housekeeping Dialog */}
      <Dialog
        open={startDialogOpen}
        onClose={() => {
          if (!submitting) {
            setStartDialogOpen(false);
            setBeforePhotos([]);
            setBeforePhotoPreviews([]);
            setNotes("");
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)",
            color: "white",
            py: 2.5,
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={2}>
            <Icon sx={{ fontSize: "2rem" }}>play_arrow</Icon>
            <MDTypography variant="h5" fontWeight="bold">
              Start Housekeeping
            </MDTypography>
          </MDBox>
          <IconButton
            onClick={() => {
              if (!submitting) {
                setStartDialogOpen(false);
                setBeforePhotos([]);
                setBeforePhotoPreviews([]);
                setNotes("");
              }
            }}
            sx={{ color: "white" }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedTask && (
            <MDBox>
              <MDTypography variant="body2" color="text.secondary" mb={3}>
                Upload photos of the property before cleaning begins.
              </MDTypography>

              {/* Before Photos Upload */}
              <MDBox mb={3}>
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  Before Photos *
                </MDTypography>
                <MDButton
                  component="label"
                  variant="outlined"
                  color="warning"
                  startIcon={<Icon>upload</Icon>}
                  sx={{ mb: 2, minHeight: "44px" }}
                >
                  Upload Before Photos
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={handleBeforePhotoChange}
                  />
                </MDButton>

                {beforePhotoPreviews.length > 0 && (
                  <Grid container spacing={2} mt={1}>
                    {beforePhotoPreviews.map((preview, index) => (
                      <Grid item xs={6} sm={4} md={3} key={index}>
                        <MDBox position="relative">
                          <img
                            src={preview}
                            alt={`Before ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                              borderRadius: 8,
                            }}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeBeforePhoto(index)}
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              bgcolor: "rgba(255,255,255,0.9)",
                            }}
                          >
                            <Icon fontSize="small">close</Icon>
                          </IconButton>
                        </MDBox>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </MDBox>

              {/* Notes */}
              <MDBox>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes (Optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about the property condition..."
                />
              </MDBox>
            </MDBox>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          <MDButton
            onClick={() => {
              if (!submitting) {
                setStartDialogOpen(false);
                setBeforePhotos([]);
                setBeforePhotoPreviews([]);
                setNotes("");
              }
            }}
            color="secondary"
            variant="outlined"
            disabled={submitting}
            sx={{ minHeight: "44px", px: 3 }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmitStart}
            color="warning"
            variant="gradient"
            disabled={submitting || beforePhotos.length === 0}
            startIcon={
              submitting ? <CircularProgress size={20} color="inherit" /> : <Icon>play_arrow</Icon>
            }
            sx={{ minHeight: "44px", px: 4 }}
          >
            {submitting ? "Starting..." : "Start Cleaning"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Complete Housekeeping Dialog */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => {
          if (!submitting) {
            setCompleteDialogOpen(false);
            setAfterPhotos([]);
            setAfterPhotoPreviews([]);
            setNotes("");
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)",
            color: "white",
            py: 2.5,
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={2}>
            <Icon sx={{ fontSize: "2rem" }}>check_circle</Icon>
            <MDTypography variant="h5" fontWeight="bold">
              Complete Housekeeping
            </MDTypography>
          </MDBox>
          <IconButton
            onClick={() => {
              if (!submitting) {
                setCompleteDialogOpen(false);
                setAfterPhotos([]);
                setAfterPhotoPreviews([]);
                setNotes("");
              }
            }}
            sx={{ color: "white" }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedTask && (
            <MDBox>
              <MDTypography variant="body2" color="text.secondary" mb={3}>
                Upload photos of the property after cleaning is complete.
              </MDTypography>

              {/* Show Before Photos if available */}
              {selectedTask.housekeeping?.beforePhotos &&
                selectedTask.housekeeping.beforePhotos.length > 0 && (
                  <MDBox mb={3}>
                    <MDTypography variant="h6" fontWeight="bold" mb={2}>
                      Before Photos
                    </MDTypography>
                    <Grid container spacing={2}>
                      {selectedTask.housekeeping.beforePhotos.map((photo, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <img
                            src={photo}
                            alt={`Before ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                              borderRadius: 8,
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </MDBox>
                )}

              {/* After Photos Upload */}
              <MDBox mb={3}>
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  After Photos *
                </MDTypography>
                <MDButton
                  component="label"
                  variant="outlined"
                  color="success"
                  startIcon={<Icon>upload</Icon>}
                  sx={{ mb: 2, minHeight: "44px" }}
                >
                  Upload After Photos
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={handleAfterPhotoChange}
                  />
                </MDButton>

                {afterPhotoPreviews.length > 0 && (
                  <Grid container spacing={2} mt={1}>
                    {afterPhotoPreviews.map((preview, index) => (
                      <Grid item xs={6} sm={4} md={3} key={index}>
                        <MDBox position="relative">
                          <img
                            src={preview}
                            alt={`After ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                              borderRadius: 8,
                            }}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeAfterPhoto(index)}
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              bgcolor: "rgba(255,255,255,0.9)",
                            }}
                          >
                            <Icon fontSize="small">close</Icon>
                          </IconButton>
                        </MDBox>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </MDBox>

              {/* Notes */}
              <MDBox>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes (Optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about the cleaning..."
                />
              </MDBox>
            </MDBox>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, bgcolor: "grey.50" }}>
          <MDButton
            onClick={() => {
              if (!submitting) {
                setCompleteDialogOpen(false);
                setAfterPhotos([]);
                setAfterPhotoPreviews([]);
                setNotes("");
              }
            }}
            color="secondary"
            variant="outlined"
            disabled={submitting}
            sx={{ minHeight: "44px", px: 3 }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSubmitComplete}
            color="success"
            variant="gradient"
            disabled={submitting || afterPhotos.length === 0}
            startIcon={
              submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon>check_circle</Icon>
              )
            }
            sx={{ minHeight: "44px", px: 4 }}
          >
            {submitting ? "Completing..." : "Complete Cleaning"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default HousekeepingDashboard;
